(function () {
  function initSlideUp(options = {}) {
    const {
      selector = '[data-slide-up]',
      loadedClass = 'slide-up-loaded',
      activeRootClass = 'slide-up-active',
      threshold = 0,
      root = null,
      rootMargin = '0px',
      groupSelector = '[data-slide-up-group]',
      staggerStepMs = 40,
      excludeClassList = [],
      excludeSelectorList = [],
      fixInitialScrollRestore = true,
      watchDom = true,
    } = options;

    const supportsIO = 'IntersectionObserver' in window;
    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let io = null;
    let mo = null;
    let refreshRafId = null;

    const excludeSelectors = [
      ...(excludeClassList || []).filter(Boolean).map((className) =>
        className.startsWith('.') ? className : '.' + className
      ),
      ...(excludeSelectorList || []).filter(Boolean),
    ];

    const qsa = (container = document, sel = selector) =>
      Array.from(container.querySelectorAll(sel));

    function shouldExclude(el) {
      if (!excludeSelectors.length) return false;
      return excludeSelectors.some((sel) => !!el.closest(sel));
    }

    function disableInContainer(container = document) {
      const els = qsa(container);
      for (const el of els) {
        if (!shouldExclude(el)) continue;
        el.removeAttribute('data-slide-up');
        el.classList.remove(loadedClass);
        delete el.dataset.slideUpDone;
        delete el.dataset.slideUpDelaySet;
      }
    }

    const groupCounters = new WeakMap();

    function getGroupEl(el) {
      const groupEl = el.closest(groupSelector);
      return groupEl || el.parentElement || document.body;
    }

    function setIncrementDelay(el) {
      if (el.dataset.slideUpDelaySet === '1') return;
      if (!el.hasAttribute('data-slide-up')) return;
      if (shouldExclude(el)) return;

      if (el.style.getPropertyValue('--su-delay')) {
        el.dataset.slideUpDelaySet = '1';
        return;
      }

      const groupEl = getGroupEl(el);
      const currentIndex = groupCounters.get(groupEl) || 0;
      el.style.setProperty('--su-delay', currentIndex * staggerStepMs + 'ms');
      el.dataset.slideUpDelaySet = '1';
      groupCounters.set(groupEl, currentIndex + 1);
    }

    function addLoadedOnce(el) {
      if (el.dataset.slideUpDone === '1') return;
      setIncrementDelay(el);
      el.classList.add(loadedClass);
      el.dataset.slideUpDone = '1';
    }

    function ensureIO() {
      if (!supportsIO || prefersReducedMotion) return null;
      if (io) return io;

      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const el = entry.target;
            if (!el.hasAttribute('data-slide-up')) continue;
            if (shouldExclude(el)) continue;
            if (!entry.isIntersecting) continue;

            addLoadedOnce(el);
            io.unobserve(el);
          }
        },
        { root, rootMargin, threshold }
      );

      return io;
    }

    function observe(container = document) {
      disableInContainer(container);

      const els = qsa(container).filter((el) => !shouldExclude(el));
      if (prefersReducedMotion || !supportsIO) {
        els.forEach(addLoadedOnce);
        return;
      }

      const observer = ensureIO();
      els.forEach((el) => observer.observe(el));
    }

    function manualCheck(container = document) {
      const els = qsa(container).filter((el) => !shouldExclude(el));
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      for (const el of els) {
        if (el.dataset.slideUpDone === '1') continue;
        const rect = el.getBoundingClientRect();
        const isInTriggerZone = rect.bottom > 0 && rect.top < viewportHeight;
        if (isInTriggerZone) addLoadedOnce(el);
      }
    }

    function refresh(container = document) {
      observe(container);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          manualCheck(container);
        });
      });
    }

    function scheduleRefresh(container = document) {
      if (refreshRafId != null) return;
      refreshRafId = window.requestAnimationFrame(() => {
        refreshRafId = null;
        refresh(container);
      });
    }

    function init(container = document) {
      manualCheck(container);
      document.documentElement.classList.add(activeRootClass);

      if (prefersReducedMotion) {
        refresh(container);
        return;
      }

      scheduleRefresh(container);

      if (fixInitialScrollRestore) {
        window.addEventListener('load', () => scheduleRefresh(container), {
          once: true,
        });
        window.addEventListener('pageshow', () => scheduleRefresh(container));
      }

      if (watchDom) {
        mo = new MutationObserver((mutations) => {
          let shouldRefresh = false;

          for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
              if (!(node instanceof Element)) continue;
              if (!node.querySelectorAll) continue;

              disableInContainer(node);
              manualCheck(node);
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) scheduleRefresh(document);
        });

        mo.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }
    }

    function destroy() {
      if (io) {
        io.disconnect();
        io = null;
      }
      if (mo) {
        mo.disconnect();
        mo = null;
      }
      if (refreshRafId != null) {
        window.cancelAnimationFrame(refreshRafId);
        refreshRafId = null;
      }
    }

    return { init, refresh, destroy };
  }

  window.initSlideUp = initSlideUp;
})();
